import { c as _c } from "react/compiler-runtime";
// Раздел «Запросы» покупателя (fd-request-*): список моих запросов на поиск и
// изготовление, форма создания, попап запроса с откликами продавцов (принять →
// заказ / отклонить), правка до первого отклика и отмена. Данные — api.buyer.*,
// авторизация — тот же телефонный вход, что и в профиле.
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { FileText, Hammer, Paperclip, Plus, Search, UserRound, X } from "lucide-react";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router";
import { toast } from "sonner";
import { api, type BuyerRequest, type BuyerRequestDetail, type BuyerRequestStatus, type BuyerRequestType, type Category, type CreateBuyerRequest, type RequestOffer } from "@/api";
import { EmptyState, ErrorState } from "@/components/common/states";
import { CustomOrderList } from "@/components/shop/custom-order";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@osmix/ui/dialog";
import { Button } from "@osmix/ui/button";
import { Input } from "@osmix/ui/input";
import { Skeleton } from "@osmix/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { useCart } from "@/store";
import { useProfile } from "@/store";
const rub = (value: number) => `${new Intl.NumberFormat("ru-RU").format(value)} руб.`;
const STATUS_STYLES: Record<BuyerRequestStatus, string> = {
  open: "bg-status-published/10 text-status-published",
  in_progress: "bg-osmix-tangelo/10 text-osmix-tangelo",
  closed: "bg-osmix-gray-200 text-osmix-text-2",
  cancelled: "bg-osmix-gray-200 text-osmix-icon"
};

// Плоский список категорий из дерева — для селекта в форме.
const flattenCategories = (nodes: Category[], depth = 0): Array<{
  id: string;
  label: string;
}> => nodes.flatMap(node => [{
  id: node.id,
  label: `${"— ".repeat(depth)}${node.name}`
}, ...flattenCategories(node.children ?? [], depth + 1)]);
export function RequestsPage() {
  const $ = _c(69);
  const {
    t
  } = useTranslation();
  const token = useProfile(_temp);
  const user = useProfile(_temp2);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const t0 = user?.id ?? "guest";
  let t1;
  if ($[0] !== t0) {
    t1 = ["buyer", "requests", t0];
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = Boolean(token && user);
  let t3;
  if ($[2] !== t1 || $[3] !== t2) {
    t3 = {
      queryKey: t1,
      queryFn: _temp3,
      enabled: t2,
      retry: false
    };
    $[2] = t1;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const list = useQuery(t3);
  if (!token || !user) {
    let t4;
    if ($[5] !== t) {
      t4 = t("requests.signInTitle");
      $[5] = t;
      $[6] = t4;
    } else {
      t4 = $[6];
    }
    let t5;
    if ($[7] !== t) {
      t5 = t("requests.signInHint");
      $[7] = t;
      $[8] = t5;
    } else {
      t5 = $[8];
    }
    let t6;
    if ($[9] !== t) {
      t6 = t("requests.goToSignIn");
      $[9] = t;
      $[10] = t6;
    } else {
      t6 = $[10];
    }
    let t7;
    if ($[11] !== t6) {
      t7 = <Button asChild={true} variant="osmix-filled" size="pill"><Link to="/profile">{t6}</Link></Button>;
      $[11] = t6;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    let t8;
    if ($[13] !== t4 || $[14] !== t5 || $[15] !== t7) {
      t8 = <div className="mx-auto max-w-5xl pt-2.5"><EmptyState icon={UserRound} title={t4} hint={t5} action={t7} /></div>;
      $[13] = t4;
      $[14] = t5;
      $[15] = t7;
      $[16] = t8;
    } else {
      t8 = $[16];
    }
    return t8;
  }
  let t4;
  if ($[17] !== list.data?.items) {
    t4 = list.data?.items ?? [];
    $[17] = list.data?.items;
    $[18] = t4;
  } else {
    t4 = $[18];
  }
  const items = t4;
  let t5;
  if ($[19] !== t) {
    t5 = t("requests.eyebrow");
    $[19] = t;
    $[20] = t5;
  } else {
    t5 = $[20];
  }
  let t6;
  if ($[21] !== t5) {
    t6 = <p className="type-small uppercase tracking-wide text-osmix-icon">{t5}</p>;
    $[21] = t5;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  let t7;
  if ($[23] !== t) {
    t7 = t("requests.title");
    $[23] = t;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== t7) {
    t8 = <h1 className="type-h2">{t7}</h1>;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  let t9;
  if ($[27] !== t) {
    t9 = t("requests.subtitle");
    $[27] = t;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  let t10;
  if ($[29] !== t9) {
    t10 = <p className="type-body2 text-osmix-text-2">{t9}</p>;
    $[29] = t9;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  let t11;
  if ($[31] !== t10 || $[32] !== t6 || $[33] !== t8) {
    t11 = <div>{t6}{t8}{t10}</div>;
    $[31] = t10;
    $[32] = t6;
    $[33] = t8;
    $[34] = t11;
  } else {
    t11 = $[34];
  }
  let t12;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => setCreateOpen(true);
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  let t13;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <Plus className="size-4" />;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== t) {
    t14 = t("requests.create");
    $[37] = t;
    $[38] = t14;
  } else {
    t14 = $[38];
  }
  let t15;
  if ($[39] !== t14) {
    t15 = <Button variant="osmix-filled" size="pill" onClick={t12} data-testid="request-create-open">{t13}{t14}</Button>;
    $[39] = t14;
    $[40] = t15;
  } else {
    t15 = $[40];
  }
  let t16;
  if ($[41] !== t11 || $[42] !== t15) {
    t16 = <section className="flex flex-col gap-4 rounded-2xl bg-card p-6 shadow-block md:flex-row md:items-center md:justify-between">{t11}{t15}</section>;
    $[41] = t11;
    $[42] = t15;
    $[43] = t16;
  } else {
    t16 = $[43];
  }
  let t17;
  if ($[44] !== items || $[45] !== list || $[46] !== t) {
    t17 = list.isError ? <ErrorState onRetry={() => void list.refetch()} /> : list.isPending ? <div className="grid gap-4"><Skeleton className="h-28 rounded-2xl" /><Skeleton className="h-28 rounded-2xl" /></div> : items.length === 0 ? <EmptyState icon={Search} title={t("requests.emptyTitle")} hint={t("requests.emptyHint")} action={<Button variant="osmix-outline" size="pill" onClick={() => setCreateOpen(true)}>{t("requests.create")}</Button>} /> : <div className="grid gap-4" data-testid="request-list">{items.map(request => <RequestCard key={request.id} request={request} onOpen={() => setSelectedId(request.id)} />)}</div>;
    $[44] = items;
    $[45] = list;
    $[46] = t;
    $[47] = t17;
  } else {
    t17 = $[47];
  }
  let t18;
  if ($[48] !== t) {
    t18 = t("shop.customOrder.allRequests");
    $[48] = t;
    $[49] = t18;
  } else {
    t18 = $[49];
  }
  let t19;
  if ($[50] !== t18) {
    t19 = <CustomOrderList title={t18} />;
    $[50] = t18;
    $[51] = t19;
  } else {
    t19 = $[51];
  }
  let t20;
  if ($[52] !== list) {
    t20 = id => {
      list.refetch();
      setSelectedId(id);
    };
    $[52] = list;
    $[53] = t20;
  } else {
    t20 = $[53];
  }
  let t21;
  if ($[54] !== createOpen || $[55] !== t20) {
    t21 = <CreateRequestDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={t20} />;
    $[54] = createOpen;
    $[55] = t20;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  let t22;
  if ($[57] === Symbol.for("react.memo_cache_sentinel")) {
    t22 = open => {
      if (!open) {
        setSelectedId(null);
      }
    };
    $[57] = t22;
  } else {
    t22 = $[57];
  }
  let t23;
  if ($[58] !== list) {
    t23 = () => void list.refetch();
    $[58] = list;
    $[59] = t23;
  } else {
    t23 = $[59];
  }
  let t24;
  if ($[60] !== selectedId || $[61] !== t23) {
    t24 = <RequestDetailDialog requestId={selectedId} onOpenChange={t22} onChanged={t23} />;
    $[60] = selectedId;
    $[61] = t23;
    $[62] = t24;
  } else {
    t24 = $[62];
  }
  let t25;
  if ($[63] !== t16 || $[64] !== t17 || $[65] !== t19 || $[66] !== t21 || $[67] !== t24) {
    t25 = <div className="mx-auto flex max-w-5xl flex-col gap-6 pt-2.5">{t16}{t17}{t19}{t21}{t24}</div>;
    $[63] = t16;
    $[64] = t17;
    $[65] = t19;
    $[66] = t21;
    $[67] = t24;
    $[68] = t25;
  } else {
    t25 = $[68];
  }
  return t25;
}
function _temp3() {
  return api.buyer.listRequests();
}
function _temp2(s_0) {
  return s_0.user;
}
function _temp(s) {
  return s.token;
}
function TypeIcon(t0) {
  const $ = _c(3);
  const {
    type,
    className
  } = t0;
  const Icon = type === "manufacture" ? Hammer : Search;
  let t1;
  if ($[0] !== Icon || $[1] !== className) {
    t1 = <Icon className={className} />;
    $[0] = Icon;
    $[1] = className;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}
function RequestCard(t0) {
  const $ = _c(52);
  const {
    request,
    onOpen
  } = t0;
  const {
    t
  } = useTranslation();
  let t1;
  if ($[0] !== request.type) {
    t1 = <TypeIcon type={request.type} className="size-3.5" />;
    $[0] = request.type;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const t2 = `requests.type.${request.type}`;
  let t3;
  if ($[2] !== t || $[3] !== t2) {
    t3 = t(t2);
    $[2] = t;
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t1 || $[6] !== t3) {
    t4 = <span className="inline-flex items-center gap-1.5 rounded-full bg-osmix-gray-100 px-2.5 py-1 type-small text-osmix-text-1">{t1}{t3}</span>;
    $[5] = t1;
    $[6] = t3;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const t5 = STATUS_STYLES[request.status];
  let t6;
  if ($[8] !== t5) {
    t6 = cn("rounded-full px-2.5 py-1 type-small", t5);
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  const t7 = `requests.status.${request.status}`;
  let t8;
  if ($[10] !== t || $[11] !== t7) {
    t8 = t(t7);
    $[10] = t;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== t6 || $[14] !== t8) {
    t9 = <span className={t6}>{t8}</span>;
    $[13] = t6;
    $[14] = t8;
    $[15] = t9;
  } else {
    t9 = $[15];
  }
  let t10;
  if ($[16] !== request.humanCode) {
    t10 = <span className="ml-auto type-small text-osmix-icon">{request.humanCode}</span>;
    $[16] = request.humanCode;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  let t11;
  if ($[18] !== t10 || $[19] !== t4 || $[20] !== t9) {
    t11 = <div className="flex flex-wrap items-center gap-2">{t4}{t9}{t10}</div>;
    $[18] = t10;
    $[19] = t4;
    $[20] = t9;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== request.title) {
    t12 = <p className="type-h4 text-osmix-text-1">{request.title}</p>;
    $[22] = request.title;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== request.description) {
    t13 = <p className="mt-1 line-clamp-2 type-body2 text-osmix-text-2">{request.description}</p>;
    $[24] = request.description;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t12 || $[27] !== t13) {
    t14 = <div>{t12}{t13}</div>;
    $[26] = t12;
    $[27] = t13;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  let t15;
  if ($[29] !== request.categoryName) {
    t15 = request.categoryName ? <span>{request.categoryName}</span> : null;
    $[29] = request.categoryName;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== request.budgetMax || $[32] !== request.budgetMin) {
    t16 = <BudgetLabel min={request.budgetMin} max={request.budgetMax} />;
    $[31] = request.budgetMax;
    $[32] = request.budgetMin;
    $[33] = t16;
  } else {
    t16 = $[33];
  }
  let t17;
  if ($[34] !== request.deadline || $[35] !== t) {
    t17 = request.deadline ? <span>{t("requests.deadlineShort", {
        date: request.deadline
      })}</span> : null;
    $[34] = request.deadline;
    $[35] = t;
    $[36] = t17;
  } else {
    t17 = $[36];
  }
  let t18;
  if ($[37] !== request.offerCount || $[38] !== t) {
    t18 = t("requests.offers", {
      count: request.offerCount
    });
    $[37] = request.offerCount;
    $[38] = t;
    $[39] = t18;
  } else {
    t18 = $[39];
  }
  let t19;
  if ($[40] !== t18) {
    t19 = <span className="ml-auto text-osmix-tangelo">{t18}</span>;
    $[40] = t18;
    $[41] = t19;
  } else {
    t19 = $[41];
  }
  let t20;
  if ($[42] !== t15 || $[43] !== t16 || $[44] !== t17 || $[45] !== t19) {
    t20 = <div className="flex flex-wrap items-center gap-x-4 gap-y-1 type-small text-osmix-icon">{t15}{t16}{t17}{t19}</div>;
    $[42] = t15;
    $[43] = t16;
    $[44] = t17;
    $[45] = t19;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] !== onOpen || $[48] !== t11 || $[49] !== t14 || $[50] !== t20) {
    t21 = <button type="button" onClick={onOpen} data-testid="request-card" className="flex flex-col gap-3 rounded-2xl bg-card p-5 text-left shadow-block outline-none transition-shadow hover:shadow-top-deep focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40">{t11}{t14}{t20}</button>;
    $[47] = onOpen;
    $[48] = t11;
    $[49] = t14;
    $[50] = t20;
    $[51] = t21;
  } else {
    t21 = $[51];
  }
  return t21;
}
function BudgetLabel(t0) {
  const $ = _c(12);
  const {
    min,
    max
  } = t0;
  const {
    t
  } = useTranslation();
  if (min == null && max == null) {
    return null;
  }
  if (min != null && max != null) {
    const t1 = `${rub(min)} — ${rub(max)}`;
    let t2;
    if ($[0] !== t1) {
      t2 = <span>{t1}</span>;
      $[0] = t1;
      $[1] = t2;
    } else {
      t2 = $[1];
    }
    return t2;
  }
  if (min != null) {
    let t1;
    if ($[2] !== min || $[3] !== t) {
      t1 = t("requests.budgetFrom", {
        value: rub(min)
      });
      $[2] = min;
      $[3] = t;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    let t2;
    if ($[5] !== t1) {
      t2 = <span>{t1}</span>;
      $[5] = t1;
      $[6] = t2;
    } else {
      t2 = $[6];
    }
    return t2;
  }
  const t1 = max as number;
  let t2;
  if ($[7] !== t || $[8] !== t1) {
    t2 = t("requests.budgetTo", {
      value: rub(t1)
    });
    $[7] = t;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== t2) {
    t3 = <span>{t2}</span>;
    $[10] = t2;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  return t3;
}
type FormState = {
  type: BuyerRequestType;
  categoryId: string;
  description: string;
  deliveryRegion: string;
  budgetMin: string;
  budgetMax: string;
  deadline: string;
};
const emptyForm: FormState = {
  type: "search",
  categoryId: "",
  description: "",
  deliveryRegion: "",
  budgetMin: "",
  budgetMax: "",
  deadline: ""
};
const toCreatePayload = (form: FormState): CreateBuyerRequest => ({
  type: form.type,
  categoryId: form.categoryId,
  description: form.description.trim(),
  deliveryRegion: form.deliveryRegion.trim() || undefined,
  budgetMin: form.budgetMin ? Number(form.budgetMin) : undefined,
  budgetMax: form.budgetMax ? Number(form.budgetMax) : undefined,
  deadline: form.deadline || undefined
});
function CreateRequestDialog({
  open,
  onOpenChange,
  onCreated
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (id: string) => void;
}) {
  const {
    t
  } = useTranslation();
  const [form, setForm] = useState<FormState>(emptyForm);
  const [files, setFiles] = useState<File[]>([]);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const categories = useQuery({
    queryKey: ["categories"],
    queryFn: () => api.categories.list(),
    staleTime: 5 * 60_000
  });
  const options = useMemo(() => flattenCategories(categories.data ?? []), [categories.data]);
  const reset = () => {
    setForm(emptyForm);
    setFiles([]);
  };
  const mutation = useMutation({
    mutationFn: async () => {
      const created = await api.buyer.createRequest(toCreatePayload(form));
      for (const file of files) {
        try {
          await api.buyer.addRequestAttachment(created.id, file);
        } catch (cause) {
          console.warn("[requests] attachment upload failed", cause);
        }
      }
      return created;
    },
    onSuccess: created_0 => {
      toast.success(t("requests.created"));
      reset();
      onOpenChange(false);
      onCreated(created_0.id);
    },
    onError: cause_0 => toast.error(cause_0 instanceof Error ? cause_0.message : t("common.error"))
  });
  const canSubmit = form.categoryId.length > 0 && form.description.trim().length >= 2;
  return <Dialog open={open} onOpenChange={next => {
    if (!next) reset();
    onOpenChange(next);
  }}>
			<DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-lg">
				<DialogHeader>
					<DialogTitle>{t("requests.create")}</DialogTitle>
					<DialogDescription>{t("requests.createHint")}</DialogDescription>
				</DialogHeader>

				<div className="flex flex-col gap-4">
					<div className="grid grid-cols-2 gap-2">
						{(["search", "manufacture"] as const).map(type => <button key={type} type="button" onClick={() => setForm(s => ({
            ...s,
            type
          }))} className={cn("flex items-center gap-2 rounded-2xl border p-3 type-body2-bold outline-none transition-colors focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40", form.type === type ? "border-osmix-tangelo bg-osmix-tangelo/5 text-osmix-tangelo" : "border-osmix-gray-300 text-osmix-text-1 hover:border-osmix-text-1")}>
								<TypeIcon type={type} className="size-4" />
								{t(`requests.type.${type}`)}
							</button>)}
					</div>

					<label className="flex flex-col gap-2 type-body2-bold">
						{t("requests.category")}
						<Select value={form.categoryId} onValueChange={value => setForm(s_0 => ({
            ...s_0,
            categoryId: value
          }))}>
							<SelectTrigger data-testid="request-category">
								<SelectValue placeholder={t("requests.categoryPlaceholder")} />
							</SelectTrigger>
							<SelectContent>
								{options.map(option => <SelectItem key={option.id} value={option.id}>
										{option.label}
									</SelectItem>)}
							</SelectContent>
						</Select>
					</label>

					<label className="flex flex-col gap-2 type-body2-bold">
						{t("requests.description")}
						<Textarea data-testid="request-description" value={form.description} onChange={e => setForm(s_1 => ({
            ...s_1,
            description: e.target.value
          }))} placeholder={t("requests.descriptionPlaceholder")} rows={4} />
					</label>

					<label className="flex flex-col gap-2 type-body2-bold">
						{t("requests.region")}
						<Input value={form.deliveryRegion} onChange={e_0 => setForm(s_2 => ({
            ...s_2,
            deliveryRegion: e_0.target.value
          }))} placeholder={t("requests.regionPlaceholder")} />
					</label>

					<div className="grid grid-cols-2 gap-3">
						<label className="flex flex-col gap-2 type-body2-bold">
							{t("requests.budgetMin")}
							<Input type="number" inputMode="numeric" min={0} value={form.budgetMin} onChange={e_1 => setForm(s_3 => ({
              ...s_3,
              budgetMin: e_1.target.value
            }))} />
						</label>
						<label className="flex flex-col gap-2 type-body2-bold">
							{t("requests.budgetMax")}
							<Input type="number" inputMode="numeric" min={0} value={form.budgetMax} onChange={e_2 => setForm(s_4 => ({
              ...s_4,
              budgetMax: e_2.target.value
            }))} />
						</label>
					</div>

					<label className="flex flex-col gap-2 type-body2-bold">
						{t("requests.deadline")}
						<Input type="date" value={form.deadline} onChange={e_3 => setForm(s_5 => ({
            ...s_5,
            deadline: e_3.target.value
          }))} />
					</label>

					<div className="flex flex-col gap-2">
						<span className="type-body2-bold">{t("requests.attachments")}</span>
						<input ref={fileInput} type="file" multiple accept="image/*,.pdf,.doc,.docx" className="hidden" onChange={e_4 => {
            setFiles(prev => [...prev, ...Array.from(e_4.target.files ?? [])]);
            if (fileInput.current) fileInput.current.value = "";
          }} />
						<Button type="button" variant="osmix-outline" size="pill" className="self-start" onClick={() => fileInput.current?.click()}>
							<Paperclip className="size-4" />
							{t("requests.attach")}
						</Button>
						{files.length > 0 && <ul className="flex flex-col gap-1">
								{files.map((file_0, index) => <li key={`${file_0.name}-${index}`} className="flex items-center gap-2 type-small text-osmix-text-2">
										<FileText className="size-4 shrink-0 text-osmix-icon" />
										<span className="truncate">{file_0.name}</span>
										<button type="button" className="ml-auto rounded-sm text-osmix-icon outline-none transition-colors hover:text-osmix-text-1 focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40" onClick={() => setFiles(prev_0 => prev_0.filter((_, i) => i !== index))} aria-label={t("common.close")}>
											<X className="size-4" />
										</button>
									</li>)}
							</ul>}
					</div>
				</div>

				<DialogFooter>
					<Button variant="osmix-outline" size="pill" onClick={() => onOpenChange(false)}>
						{t("common.cancel")}
					</Button>
					<Button variant="osmix-filled" size="pill" disabled={!canSubmit || mutation.isPending} onClick={() => mutation.mutate()} data-testid="request-submit">
						{t("requests.publish")}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>;
}
function RequestDetailDialog(t0) {
  const $ = _c(67);
  const {
    requestId,
    onOpenChange,
    onChanged
  } = t0;
  const {
    t
  } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const acceptOfferInCart = useCart(_temp6);
  const [editing, setEditing] = useState(false);
  const t1 = requestId ?? "none";
  let t2;
  if ($[0] !== t1) {
    t2 = ["buyer", "request", t1];
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== requestId) {
    t3 = () => api.buyer.getRequest(requestId ?? "");
    $[2] = requestId;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const t4 = Boolean(requestId);
  let t5;
  if ($[4] !== t2 || $[5] !== t3 || $[6] !== t4) {
    t5 = {
      queryKey: t2,
      queryFn: t3,
      enabled: t4,
      retry: false
    };
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const detail = useQuery(t5);
  const request = detail.data;
  let t6;
  if ($[8] !== detail || $[9] !== onChanged) {
    t6 = async () => {
      await detail.refetch();
      onChanged();
    };
    $[8] = detail;
    $[9] = onChanged;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const refresh = t6;
  let t7;
  if ($[11] !== requestId) {
    t7 = () => api.buyer.cancelRequest(requestId ?? "");
    $[11] = requestId;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] !== refresh || $[14] !== t) {
    t8 = async () => {
      toast.success(t("requests.cancelled"));
      await refresh();
    };
    $[13] = refresh;
    $[14] = t;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t) {
    t9 = cause => toast.error(cause instanceof Error ? cause.message : t("common.error"));
    $[16] = t;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t7 || $[19] !== t8 || $[20] !== t9) {
    t10 = {
      mutationFn: t7,
      onSuccess: t8,
      onError: t9
    };
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const cancel = useMutation(t10);
  let t11;
  if ($[22] !== acceptOfferInCart) {
    t11 = async offer => offer.productId ? {
      kind: "cart" as const,
      cart: await acceptOfferInCart(offer.id)
    } : {
      kind: "chat" as const,
      offer: await api.buyer.acceptOfferInChat(offer.id)
    };
    $[22] = acceptOfferInCart;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== navigate || $[25] !== onOpenChange || $[26] !== queryClient || $[27] !== refresh || $[28] !== t) {
    t12 = async result => {
      toast.success(t(result.kind === "cart" ? "requests.offerAccepted" : "requests.offerAcceptedInChat"));
      await refresh();
      queryClient.invalidateQueries({
        queryKey: ["buyer", "requests"]
      });
      onOpenChange(false);
      navigate(result.kind === "cart" ? "/cart" : `/chats?conversation=${encodeURIComponent(result.offer.conversationId)}`);
    };
    $[24] = navigate;
    $[25] = onOpenChange;
    $[26] = queryClient;
    $[27] = refresh;
    $[28] = t;
    $[29] = t12;
  } else {
    t12 = $[29];
  }
  let t13;
  if ($[30] !== t) {
    t13 = cause_0 => toast.error(cause_0 instanceof Error ? cause_0.message : t("common.error"));
    $[30] = t;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  let t14;
  if ($[32] !== t11 || $[33] !== t12 || $[34] !== t13) {
    t14 = {
      mutationFn: t11,
      onSuccess: t12,
      onError: t13
    };
    $[32] = t11;
    $[33] = t12;
    $[34] = t13;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  const accept = useMutation(t14);
  let t15;
  if ($[36] !== refresh || $[37] !== t) {
    t15 = async () => {
      toast.success(t("requests.offerDeclined"));
      await refresh();
    };
    $[36] = refresh;
    $[37] = t;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== t) {
    t16 = cause_1 => toast.error(cause_1 instanceof Error ? cause_1.message : t("common.error"));
    $[39] = t;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== t15 || $[42] !== t16) {
    t17 = {
      mutationFn: _temp7,
      onSuccess: t15,
      onError: t16
    };
    $[41] = t15;
    $[42] = t16;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  const decline = useMutation(t17);
  const t18 = Boolean(requestId);
  let t19;
  if ($[44] !== onOpenChange) {
    t19 = open => {
      if (!open) {
        setEditing(false);
      }
      onOpenChange(open);
    };
    $[44] = onOpenChange;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== detail.isError || $[47] !== detail.isPending || $[48] !== request || $[49] !== t) {
    t20 = detail.isPending || detail.isError || !request ? <DialogTitle className="sr-only">{t("requests.title")}</DialogTitle> : null;
    $[46] = detail.isError;
    $[47] = detail.isPending;
    $[48] = request;
    $[49] = t;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  let t21;
  if ($[51] !== accept || $[52] !== cancel || $[53] !== decline || $[54] !== detail || $[55] !== editing || $[56] !== refresh || $[57] !== request || $[58] !== t) {
    t21 = detail.isPending ? <div className="flex flex-col gap-3 py-4"><Skeleton className="h-8 w-40 rounded-full" /><Skeleton className="h-40 rounded-2xl" /></div> : detail.isError || !request ? <div className="py-6"><ErrorState onRetry={() => void detail.refetch()} /></div> : editing ? <EditRequestForm request={request} onCancel={() => setEditing(false)} onSaved={async () => {
      setEditing(false);
      await refresh();
    }} /> : <><DialogHeader><div className="flex items-center gap-2"><span className="inline-flex items-center gap-1.5 rounded-full bg-osmix-gray-100 px-2.5 py-1 type-small text-osmix-text-1"><TypeIcon type={request.type} className="size-3.5" />{t(`requests.type.${request.type}`)}</span><span className={cn("rounded-full px-2.5 py-1 type-small", STATUS_STYLES[request.status])}>{t(`requests.status.${request.status}`)}</span><span className="ml-auto type-small text-osmix-icon">{request.humanCode}</span></div><DialogTitle className="mt-2">{request.title}</DialogTitle></DialogHeader><div className="flex flex-col gap-4"><p className="whitespace-pre-line type-body2 text-osmix-text-1">{request.description}</p><dl className="grid grid-cols-2 gap-3 rounded-2xl bg-osmix-gray-100/60 p-4 type-body2">{request.categoryName ? <div><dt className="type-small text-osmix-icon">{t("requests.category")}</dt><dd className="text-osmix-text-1">{request.categoryName}</dd></div> : null}{request.deliveryRegion ? <div><dt className="type-small text-osmix-icon">{t("requests.region")}</dt><dd className="text-osmix-text-1">{request.deliveryRegion}</dd></div> : null}{request.budgetMin != null || request.budgetMax != null ? <div><dt className="type-small text-osmix-icon">{t("requests.budget")}</dt><dd className="text-osmix-text-1"><BudgetLabel min={request.budgetMin} max={request.budgetMax} /></dd></div> : null}{request.deadline ? <div><dt className="type-small text-osmix-icon">{t("requests.deadline")}</dt><dd className="text-osmix-text-1">{request.deadline}</dd></div> : null}</dl>{request.attachments.length > 0 && <div className="flex flex-wrap gap-2">{request.attachments.map(attachment => <a key={attachment.id} href={attachment.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 rounded-full border border-osmix-gray-300 px-3 py-1.5 type-small text-osmix-text-1 outline-none transition-colors hover:border-osmix-text-1 focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40"><Paperclip className="size-3.5" />{attachment.kind === "image" ? t("requests.image") : t("requests.file")}</a>)}</div>}<section className="flex flex-col gap-3"><h3 className="type-h4">{t("requests.offersTitle", {
              count: request.offers.length
            })}</h3>{request.offers.length === 0 ? <p className="rounded-2xl bg-osmix-gray-100/60 p-4 type-body2 text-osmix-text-2">{t("requests.noOffers")}</p> : <div className="flex flex-col gap-3" data-testid="request-offers">{request.offers.map(offer_0 => <OfferCard key={offer_0.id} offer={offer_0} disabled={accept.isPending || decline.isPending} onAccept={() => accept.mutate(offer_0)} onDecline={() => decline.mutate(offer_0.id)} />)}</div>}</section></div><DialogFooter className="sm:justify-between"><div className="flex gap-2">{request.editable && <Button variant="osmix-outline" size="pill" onClick={() => setEditing(true)} data-testid="request-edit">{t("requests.edit")}</Button>}</div>{request.status === "open" || request.status === "in_progress" ? <Button variant="osmix-outline" size="pill" disabled={cancel.isPending} onClick={() => cancel.mutate()} data-testid="request-cancel">{t("requests.cancel")}</Button> : null}</DialogFooter></>;
    $[51] = accept;
    $[52] = cancel;
    $[53] = decline;
    $[54] = detail;
    $[55] = editing;
    $[56] = refresh;
    $[57] = request;
    $[58] = t;
    $[59] = t21;
  } else {
    t21 = $[59];
  }
  let t22;
  if ($[60] !== t20 || $[61] !== t21) {
    t22 = <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="request-detail">{t20}{t21}</DialogContent>;
    $[60] = t20;
    $[61] = t21;
    $[62] = t22;
  } else {
    t22 = $[62];
  }
  let t23;
  if ($[63] !== t18 || $[64] !== t19 || $[65] !== t22) {
    t23 = <Dialog open={t18} onOpenChange={t19}>{t22}</Dialog>;
    $[63] = t18;
    $[64] = t19;
    $[65] = t22;
    $[66] = t23;
  } else {
    t23 = $[66];
  }
  return t23;
}
function _temp7(offerId) {
  return api.buyer.declineOffer(offerId);
}
function _temp6(s) {
  return s.acceptOffer;
}
function OfferCard(t0) {
  const $ = _c(46);
  const {
    offer,
    disabled,
    onAccept,
    onDecline
  } = t0;
  const {
    t
  } = useTranslation();
  const isActive = offer.status === "active";
  let t1;
  if ($[0] !== offer.shopName) {
    t1 = <p className="type-body2-bold text-osmix-text-1">{offer.shopName}</p>;
    $[0] = offer.shopName;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== offer.description) {
    t2 = <p className="mt-1 type-body2 text-osmix-text-1">{offer.description}</p>;
    $[2] = offer.description;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = offer.quantity;
  let t4;
  if ($[4] !== offer.unitPrice) {
    t4 = rub(offer.unitPrice);
    $[4] = offer.unitPrice;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = offer.productionEstimate ? ` · ${offer.productionEstimate}` : "";
  let t6;
  if ($[6] !== offer.quantity || $[7] !== t4 || $[8] !== t5) {
    t6 = <p className="mt-1 type-small text-osmix-icon">{t3} × {t4}{t5}</p>;
    $[6] = offer.quantity;
    $[7] = t4;
    $[8] = t5;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t1 || $[11] !== t2 || $[12] !== t6) {
    t7 = <div className="min-w-0">{t1}{t2}{t6}</div>;
    $[10] = t1;
    $[11] = t2;
    $[12] = t6;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== isActive || $[15] !== offer.status || $[16] !== t) {
    t8 = !isActive && <span className="shrink-0 rounded-full bg-osmix-gray-200 px-2.5 py-1 type-small text-osmix-text-2">{t(`requests.offerStatus.${offer.status}`)}</span>;
    $[14] = isActive;
    $[15] = offer.status;
    $[16] = t;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  let t9;
  if ($[18] !== t7 || $[19] !== t8) {
    t9 = <div className="flex items-start justify-between gap-3">{t7}{t8}</div>;
    $[18] = t7;
    $[19] = t8;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== offer.productId || $[22] !== offer.productName || $[23] !== t) {
    t10 = offer.productName ? <Link to={`/product/${offer.productId}`} className="rounded-sm type-small text-osmix-tangelo outline-none transition-colors hover:underline focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40">{offer.productName}</Link> : <p className="type-small text-osmix-text-2" data-testid="offer-no-product">{t("requests.offerNoProduct")}</p>;
    $[21] = offer.productId;
    $[22] = offer.productName;
    $[23] = t;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== offer.photos || $[26] !== t) {
    t11 = offer.photos && offer.photos.length > 0 ? <ul className="flex flex-wrap gap-2" aria-label={t("requests.offerPhotos")} data-testid="offer-photos">{offer.photos.map(_temp8)}</ul> : null;
    $[25] = offer.photos;
    $[26] = t;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  let t12;
  if ($[28] !== offer.conversationId || $[29] !== offer.productId || $[30] !== offer.status || $[31] !== t) {
    t12 = offer.status === "accepted" && !offer.productId ? <Link to={`/chats?conversation=${encodeURIComponent(offer.conversationId)}`} className="self-start rounded-sm type-small text-osmix-tangelo outline-none hover:underline focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40">{t("requests.openChat")}</Link> : null;
    $[28] = offer.conversationId;
    $[29] = offer.productId;
    $[30] = offer.status;
    $[31] = t;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== disabled || $[34] !== isActive || $[35] !== offer.productId || $[36] !== onAccept || $[37] !== onDecline || $[38] !== t) {
    t13 = isActive && <div className="flex flex-wrap gap-2"><Button variant="osmix-filled" size="pill-sm" disabled={disabled} onClick={onAccept} data-testid="offer-accept">{t(offer.productId ? "requests.acceptOffer" : "requests.acceptOfferInChat")}</Button><Button variant="osmix-outline" size="pill-sm" disabled={disabled} onClick={onDecline} data-testid="offer-decline">{t("requests.declineOffer")}</Button></div>;
    $[33] = disabled;
    $[34] = isActive;
    $[35] = offer.productId;
    $[36] = onAccept;
    $[37] = onDecline;
    $[38] = t;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== t10 || $[41] !== t11 || $[42] !== t12 || $[43] !== t13 || $[44] !== t9) {
    t14 = <div className="flex flex-col gap-3 rounded-2xl border border-osmix-gray-300 p-4" data-testid="request-offer">{t9}{t10}{t11}{t12}{t13}</div>;
    $[40] = t10;
    $[41] = t11;
    $[42] = t12;
    $[43] = t13;
    $[44] = t9;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  return t14;
}
function _temp8(url) {
  return <li key={url}><a href={url} target="_blank" rel="noreferrer" className="block size-16 overflow-hidden rounded-lg bg-osmix-gray-100 outline-none focus-visible:ring-2 focus-visible:ring-[#1F1F1F]/40"><img src={url} alt="" loading="lazy" className="size-full object-cover" /></a></li>;
}
function EditRequestForm(t0) {
  const $ = _c(115);
  const {
    request,
    onCancel,
    onSaved
  } = t0;
  const {
    t
  } = useTranslation();
  const t1 = request.budgetMin != null ? String(request.budgetMin) : "";
  const t2 = request.budgetMax != null ? String(request.budgetMax) : "";
  const t3 = request.deadline ?? "";
  let t4;
  if ($[0] !== request.deliveryRegion || $[1] !== request.description || $[2] !== request.title || $[3] !== t1 || $[4] !== t2 || $[5] !== t3) {
    t4 = {
      title: request.title,
      description: request.description,
      deliveryRegion: request.deliveryRegion,
      budgetMin: t1,
      budgetMax: t2,
      deadline: t3
    };
    $[0] = request.deliveryRegion;
    $[1] = request.description;
    $[2] = request.title;
    $[3] = t1;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const [form, setForm] = useState(t4);
  let t5;
  if ($[7] !== form.budgetMax || $[8] !== form.budgetMin || $[9] !== form.deadline || $[10] !== form.deliveryRegion || $[11] !== form.description || $[12] !== form.title || $[13] !== request.id) {
    t5 = () => api.buyer.updateRequest(request.id, {
      title: form.title.trim() || undefined,
      description: form.description.trim(),
      deliveryRegion: form.deliveryRegion.trim(),
      budgetMin: form.budgetMin ? Number(form.budgetMin) : null,
      budgetMax: form.budgetMax ? Number(form.budgetMax) : null,
      deadline: form.deadline || null
    });
    $[7] = form.budgetMax;
    $[8] = form.budgetMin;
    $[9] = form.deadline;
    $[10] = form.deliveryRegion;
    $[11] = form.description;
    $[12] = form.title;
    $[13] = request.id;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== onSaved || $[16] !== t) {
    t6 = async () => {
      toast.success(t("requests.saved"));
      await onSaved();
    };
    $[15] = onSaved;
    $[16] = t;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== t) {
    t7 = cause => toast.error(cause instanceof Error ? cause.message : t("common.error"));
    $[18] = t;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t5 || $[21] !== t6 || $[22] !== t7) {
    t8 = {
      mutationFn: t5,
      onSuccess: t6,
      onError: t7
    };
    $[20] = t5;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const save = useMutation(t8);
  let t9;
  if ($[24] !== t) {
    t9 = t("requests.editTitle");
    $[24] = t;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t9) {
    t10 = <DialogTitle>{t9}</DialogTitle>;
    $[26] = t9;
    $[27] = t10;
  } else {
    t10 = $[27];
  }
  let t11;
  if ($[28] !== t) {
    t11 = t("requests.editHint");
    $[28] = t;
    $[29] = t11;
  } else {
    t11 = $[29];
  }
  let t12;
  if ($[30] !== t11) {
    t12 = <DialogDescription>{t11}</DialogDescription>;
    $[30] = t11;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== t10 || $[33] !== t12) {
    t13 = <DialogHeader>{t10}{t12}</DialogHeader>;
    $[32] = t10;
    $[33] = t12;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  let t14;
  if ($[35] !== t) {
    t14 = t("requests.titleField");
    $[35] = t;
    $[36] = t14;
  } else {
    t14 = $[36];
  }
  let t15;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = e => setForm(s => ({
      ...s,
      title: e.target.value
    }));
    $[37] = t15;
  } else {
    t15 = $[37];
  }
  let t16;
  if ($[38] !== form.title) {
    t16 = <Input value={form.title} onChange={t15} />;
    $[38] = form.title;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== t14 || $[41] !== t16) {
    t17 = <label className="flex flex-col gap-2 type-body2-bold">{t14}{t16}</label>;
    $[40] = t14;
    $[41] = t16;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== t) {
    t18 = t("requests.description");
    $[43] = t;
    $[44] = t18;
  } else {
    t18 = $[44];
  }
  let t19;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = e_0 => setForm(s_0 => ({
      ...s_0,
      description: e_0.target.value
    }));
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== form.description) {
    t20 = <Textarea value={form.description} onChange={t19} rows={4} />;
    $[46] = form.description;
    $[47] = t20;
  } else {
    t20 = $[47];
  }
  let t21;
  if ($[48] !== t18 || $[49] !== t20) {
    t21 = <label className="flex flex-col gap-2 type-body2-bold">{t18}{t20}</label>;
    $[48] = t18;
    $[49] = t20;
    $[50] = t21;
  } else {
    t21 = $[50];
  }
  let t22;
  if ($[51] !== t) {
    t22 = t("requests.region");
    $[51] = t;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  let t23;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = e_1 => setForm(s_1 => ({
      ...s_1,
      deliveryRegion: e_1.target.value
    }));
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== form.deliveryRegion) {
    t24 = <Input value={form.deliveryRegion} onChange={t23} />;
    $[54] = form.deliveryRegion;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== t22 || $[57] !== t24) {
    t25 = <label className="flex flex-col gap-2 type-body2-bold">{t22}{t24}</label>;
    $[56] = t22;
    $[57] = t24;
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  let t26;
  if ($[59] !== t) {
    t26 = t("requests.budgetMin");
    $[59] = t;
    $[60] = t26;
  } else {
    t26 = $[60];
  }
  let t27;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = e_2 => setForm(s_2 => ({
      ...s_2,
      budgetMin: e_2.target.value
    }));
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  let t28;
  if ($[62] !== form.budgetMin) {
    t28 = <Input type="number" min={0} value={form.budgetMin} onChange={t27} />;
    $[62] = form.budgetMin;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  let t29;
  if ($[64] !== t26 || $[65] !== t28) {
    t29 = <label className="flex flex-col gap-2 type-body2-bold">{t26}{t28}</label>;
    $[64] = t26;
    $[65] = t28;
    $[66] = t29;
  } else {
    t29 = $[66];
  }
  let t30;
  if ($[67] !== t) {
    t30 = t("requests.budgetMax");
    $[67] = t;
    $[68] = t30;
  } else {
    t30 = $[68];
  }
  let t31;
  if ($[69] === Symbol.for("react.memo_cache_sentinel")) {
    t31 = e_3 => setForm(s_3 => ({
      ...s_3,
      budgetMax: e_3.target.value
    }));
    $[69] = t31;
  } else {
    t31 = $[69];
  }
  let t32;
  if ($[70] !== form.budgetMax) {
    t32 = <Input type="number" min={0} value={form.budgetMax} onChange={t31} />;
    $[70] = form.budgetMax;
    $[71] = t32;
  } else {
    t32 = $[71];
  }
  let t33;
  if ($[72] !== t30 || $[73] !== t32) {
    t33 = <label className="flex flex-col gap-2 type-body2-bold">{t30}{t32}</label>;
    $[72] = t30;
    $[73] = t32;
    $[74] = t33;
  } else {
    t33 = $[74];
  }
  let t34;
  if ($[75] !== t29 || $[76] !== t33) {
    t34 = <div className="grid grid-cols-2 gap-3">{t29}{t33}</div>;
    $[75] = t29;
    $[76] = t33;
    $[77] = t34;
  } else {
    t34 = $[77];
  }
  let t35;
  if ($[78] !== t) {
    t35 = t("requests.deadline");
    $[78] = t;
    $[79] = t35;
  } else {
    t35 = $[79];
  }
  let t36;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t36 = e_4 => setForm(s_4 => ({
      ...s_4,
      deadline: e_4.target.value
    }));
    $[80] = t36;
  } else {
    t36 = $[80];
  }
  let t37;
  if ($[81] !== form.deadline) {
    t37 = <Input type="date" value={form.deadline} onChange={t36} />;
    $[81] = form.deadline;
    $[82] = t37;
  } else {
    t37 = $[82];
  }
  let t38;
  if ($[83] !== t35 || $[84] !== t37) {
    t38 = <label className="flex flex-col gap-2 type-body2-bold">{t35}{t37}</label>;
    $[83] = t35;
    $[84] = t37;
    $[85] = t38;
  } else {
    t38 = $[85];
  }
  let t39;
  if ($[86] !== t17 || $[87] !== t21 || $[88] !== t25 || $[89] !== t34 || $[90] !== t38) {
    t39 = <div className="flex flex-col gap-4">{t17}{t21}{t25}{t34}{t38}</div>;
    $[86] = t17;
    $[87] = t21;
    $[88] = t25;
    $[89] = t34;
    $[90] = t38;
    $[91] = t39;
  } else {
    t39 = $[91];
  }
  let t40;
  if ($[92] !== t) {
    t40 = t("common.cancel");
    $[92] = t;
    $[93] = t40;
  } else {
    t40 = $[93];
  }
  let t41;
  if ($[94] !== onCancel || $[95] !== t40) {
    t41 = <Button variant="osmix-outline" size="pill" onClick={onCancel}>{t40}</Button>;
    $[94] = onCancel;
    $[95] = t40;
    $[96] = t41;
  } else {
    t41 = $[96];
  }
  let t42;
  if ($[97] !== form.description || $[98] !== save.isPending) {
    t42 = save.isPending || form.description.trim().length < 2;
    $[97] = form.description;
    $[98] = save.isPending;
    $[99] = t42;
  } else {
    t42 = $[99];
  }
  let t43;
  if ($[100] !== save) {
    t43 = () => save.mutate();
    $[100] = save;
    $[101] = t43;
  } else {
    t43 = $[101];
  }
  let t44;
  if ($[102] !== t) {
    t44 = t("requests.save");
    $[102] = t;
    $[103] = t44;
  } else {
    t44 = $[103];
  }
  let t45;
  if ($[104] !== t42 || $[105] !== t43 || $[106] !== t44) {
    t45 = <Button variant="osmix-filled" size="pill" disabled={t42} onClick={t43} data-testid="request-edit-save">{t44}</Button>;
    $[104] = t42;
    $[105] = t43;
    $[106] = t44;
    $[107] = t45;
  } else {
    t45 = $[107];
  }
  let t46;
  if ($[108] !== t41 || $[109] !== t45) {
    t46 = <DialogFooter>{t41}{t45}</DialogFooter>;
    $[108] = t41;
    $[109] = t45;
    $[110] = t46;
  } else {
    t46 = $[110];
  }
  let t47;
  if ($[111] !== t13 || $[112] !== t39 || $[113] !== t46) {
    t47 = <>{t13}{t39}{t46}</>;
    $[111] = t13;
    $[112] = t39;
    $[113] = t46;
    $[114] = t47;
  } else {
    t47 = $[114];
  }
  return t47;
}